// Custom lobby state of the running match: who is in the room, who hosts it, the room rules and the
// phase. `LobbyModel` folds the reliable server messages into a plain `LobbyView` (pure, unit tested);
// the lobby panel in ui/screens/Lobby.tsx reads the `lobbyView` signal and calls the `lobby*` actions,
// which the game session registers (it owns the connection).

import { signal } from "@preact/signals";
import type { BotLevel, MatchPhase as HudPhase, TeamTag } from "../hud/types";
import { GameMode, RoomKind } from "../proto/gunrun/v1/common_pb";
import type { PlayerInfo, RoomSettings, Welcome } from "../proto/gunrun/v1/game_pb";
import { botLevel, hudPhase, teamTag } from "./convert";

/** The rules of the room the host can change (the rest of `RoomSettings` is fixed at creation). */
export interface LobbyRules {
  name: string;
  mode: GameMode;
  map: string;
  maxPlayers: number;
  scoreLimit: number;
  timeLimitSec: number;
  roundsToWin: number;
  friendlyFire: boolean;
  listed: boolean;
  hasPassword: boolean;
}

/** Rules the host edits as a draft and applies together. */
export type LobbyRulesPatch = Partial<Pick<LobbyRules, "maxPlayers" | "scoreLimit" | "timeLimitSec" | "roundsToWin" | "friendlyFire">>;

export interface LobbyPlayer {
  id: number;
  name: string;
  team: TeamTag;
  level: number;
  /** Set for a bot (the host can remove it). */
  bot: BotLevel | null;
  isHost: boolean;
  isMe: boolean;
  /** Last scoreboard ping; null until the first scoreboard arrives. */
  pingMs: number | null;
}

export interface LobbyView {
  roomName: string;
  myId: number;
  hostId: number | null;
  isHost: boolean;
  phase: HudPhase;
  rules: LobbyRules;
  /** Teams exist (everything but free-for-all). */
  teamMode: boolean;
  /** Most players per team (0 in free-for-all). */
  teamSize: number;
  /** Team A, then team B, then players without a team; stable order inside each group (host first, then id). */
  players: LobbyPlayer[];
  teamCounts: { a: number; b: number };
  /** The host may change the rules and the map / mode (the server only accepts it while the lobby waits). */
  canEdit: boolean;
  /** The host may start a match (in warmup, or from the end screen). */
  canStart: boolean;
  /** Everybody may pick a team (team modes, while the lobby waits). */
  canPickTeam: boolean;
  /** Bots in the room, and what the host may do with them. */
  botCount: number;
  canAddBots: boolean;
}

/** Players per team for `maxPlayers`; mirrors `team_size_for` in the server (rooms/spec.rs). */
export function teamSizeFor(mode: GameMode, maxPlayers: number): number {
  return mode === GameMode.FFA || mode === GameMode.UNSPECIFIED ? 0 : Math.ceil(maxPlayers / 2);
}

export function rulesOf(s: RoomSettings): LobbyRules {
  return {
    name: s.name,
    mode: s.mode,
    map: s.map,
    maxPlayers: s.maxPlayers,
    scoreLimit: s.scoreLimit,
    timeLimitSec: s.timeLimitSec,
    roundsToWin: s.roundsToWin,
    friendlyFire: s.friendlyFire,
    listed: s.listed,
    hasPassword: s.hasPassword,
  };
}

/** Whether `team` has no free slot for the local player (`mine` = the local player already sits there). */
export function teamIsFull(view: LobbyView, team: "a" | "b"): boolean {
  const mine = view.players.some((p) => p.isMe && p.team === team);
  const others = view.teamCounts[team] - (mine ? 1 : 0);
  return others >= view.teamSize;
}

const TEAM_ORDER: Record<TeamTag, number> = { a: 0, b: 1, none: 2 };

interface Entry {
  id: number;
  name: string;
  team: TeamTag;
  level: number;
  bot: BotLevel | null;
  pingMs: number | null;
}

export class LobbyModel {
  private kind: RoomKind = RoomKind.UNSPECIFIED;
  private myId = -1;
  private hostId: number | null = null;
  private rules: LobbyRules | null = null;
  private phase: HudPhase = "warmup";
  private readonly players = new Map<number, Entry>();
  /** Counts every time the lobby (re)enters warmup; the session opens the panel once per value. */
  epoch = 0;

  /** Whether this room is a player-hosted lobby (the only kind with a lobby panel). */
  get isCustom(): boolean {
    return this.kind === RoomKind.CUSTOM;
  }

  /** Back to "no room" (the connection ended). */
  clear(): void {
    this.kind = RoomKind.UNSPECIFIED;
    this.myId = -1;
    this.hostId = null;
    this.rules = null;
    this.phase = "warmup";
    this.players.clear();
  }

  /** First Welcome of the connection, or the next one after a map change (everything is rebuilt). */
  reset(w: Welcome): void {
    this.kind = w.roomKind;
    this.myId = w.yourId;
    this.hostId = w.hostId ?? null;
    this.rules = w.settings ? rulesOf(w.settings) : null;
    this.phase = hudPhase(w.phase);
    this.players.clear();
    for (const p of w.players) this.upsert(p);
    if (w.you) this.upsert(w.you);
    if (this.phase === "warmup") this.epoch++;
  }

  upsert(info: PlayerInfo): void {
    const prev = this.players.get(info.id);
    this.players.set(info.id, {
      id: info.id,
      name: info.name,
      team: teamTag(info.team),
      level: info.level,
      bot: botLevel(info.isBot, info.botDifficulty),
      pingMs: prev?.pingMs ?? null,
    });
    if (info.isHost) this.hostId = info.id;
  }

  remove(id: number): void {
    this.players.delete(id);
    if (this.hostId === id) this.hostId = null; // HostChanged follows
  }

  setHost(id: number): void {
    this.hostId = id;
  }

  setRules(s: RoomSettings): void {
    this.rules = rulesOf(s);
  }

  setPhase(phase: HudPhase): void {
    if (phase === "warmup" && this.phase !== "warmup") this.epoch++;
    this.phase = phase;
  }

  setPings(entries: readonly { id: number; pingMs: number }[]): void {
    for (const e of entries) {
      const p = this.players.get(e.id);
      if (p) p.pingMs = Math.round(e.pingMs);
    }
  }

  /** The panel's data; null outside custom lobbies (or before the first Welcome). */
  view(): LobbyView | null {
    const rules = this.rules;
    if (!this.isCustom || rules === null) return null;
    const teamMode = rules.mode !== GameMode.FFA && rules.mode !== GameMode.UNSPECIFIED;
    const players: LobbyPlayer[] = [...this.players.values()].map((p) => ({
      ...p,
      team: teamMode ? p.team : "none",
      isHost: p.id === this.hostId,
      isMe: p.id === this.myId,
    }));
    players.sort(
      (x, y) =>
        TEAM_ORDER[x.team] - TEAM_ORDER[y.team] || Number(y.isHost) - Number(x.isHost) || x.id - y.id,
    );
    const isHost = this.hostId !== null && this.hostId === this.myId;
    const teamCounts = { a: 0, b: 0 };
    for (const p of players) if (p.team !== "none") teamCounts[p.team]++;
    const warmup = this.phase === "warmup";
    return {
      roomName: rules.name,
      myId: this.myId,
      hostId: this.hostId,
      isHost,
      phase: this.phase,
      rules,
      teamMode,
      teamSize: teamSizeFor(rules.mode, rules.maxPlayers),
      players,
      teamCounts,
      canEdit: isHost && warmup,
      canStart: isHost && (warmup || this.phase === "ended"),
      canPickTeam: teamMode && warmup,
      botCount: players.filter((p) => p.bot !== null).length,
      canAddBots: isHost && players.length < rules.maxPlayers,
    };
  }
}

/** The end screen's line in a custom lobby: the host restarts from the lobby panel, everyone else waits. */
export function endScreenLobbyHint(isHost: boolean, key: string): string {
  if (!isHost) return "Back in the lobby next: the host starts the next match";
  return key === "" ? "Open the lobby from the pause menu to start the next match" : `Press ${key} to open the lobby and start the next match`;
}

// ---- Signals and actions shared with the UI -------------------------------------------------------

/** The lobby panel's data; null outside custom lobbies. Written by the game session. */
export const lobbyView = signal<LobbyView | null>(null);

export interface LobbyActions {
  startMatch(): void;
  setMap(map: string): void;
  setMode(mode: GameMode): void;
  /** Applies the changed rules together with the rest of the current settings. */
  setRules(patch: LobbyRulesPatch): void;
  kick(id: number): void;
  selectTeam(team: "a" | "b"): void;
  addBot(team: "a" | "b" | "none", level: BotLevel): void;
  removeBot(id: number): void;
}

let actions: LobbyActions | null = null;

export function registerLobbyActions(a: LobbyActions | null): void {
  actions = a;
}

export function lobbyStartMatch(): void {
  actions?.startMatch();
}

export function lobbySetMap(map: string): void {
  actions?.setMap(map);
}

export function lobbySetMode(mode: GameMode): void {
  actions?.setMode(mode);
}

export function lobbySetRules(patch: LobbyRulesPatch): void {
  actions?.setRules(patch);
}

export function lobbyKick(id: number): void {
  actions?.kick(id);
}

export function lobbySelectTeam(team: "a" | "b"): void {
  actions?.selectTeam(team);
}

export function lobbyAddBot(team: "a" | "b" | "none", level: BotLevel): void {
  actions?.addBot(team, level);
}

export function lobbyRemoveBot(id: number): void {
  actions?.removeBot(id);
}

/** Difficulty the host's "Add bot" buttons use. */
export const lobbyBotLevel = signal<BotLevel>("normal");
